/**
 * apps/web :: lib :: error-reporter
 *
 * Wave 14k (ROADS-348) — minimal frontend error capture pipeline.
 *
 * Three responsibilities, ALL fail-soft:
 *   1. Listen to `window.error` + `window.unhandledrejection` and POST a
 *      sanitised payload to the backend's `POST /api/errors` sink.
 *   2. Provide `report(error, componentStack?)` for the React
 *      ErrorBoundary to call directly when it catches an exception.
 *   3. Track the most-recent backend `X-Request-Id` so reports can be
 *      correlated with concurrent server activity. The api.ts wrapper
 *      calls `setLastApiRequestId()` after every fetch.
 *
 * Privacy: we strip PII before sending — only the message + stack survive.
 * Form input values, URL query strings, etc. are NOT included. The backend
 * has a defense-in-depth email regex on top of this rule.
 *
 * The reporter MUST NOT loop on its own failure. If the POST to /api/errors
 * itself throws (network down, backend 500), we swallow silently — no
 * second attempt, no console.error, nothing that could re-trigger the
 * window.error handler.
 */
import { resolveUrl } from "@/lib/api";

/** Module-level state. Reset between unit tests via the helpers below. */
let installed = false;
let lastApiRequestId: string | null = null;
let inFlight = false;
let errorHandler: ((evt: ErrorEvent) => void) | null = null;
let rejectionHandler: ((evt: PromiseRejectionEvent) => void) | null = null;

/** Set by api.ts after every fetch (best-effort — header may be absent). */
export function setLastApiRequestId(id: string | null): void {
  lastApiRequestId = id;
}

/** Install the global error + unhandledrejection listeners. Idempotent. */
export function installErrorReporter(): void {
  if (installed || typeof window === "undefined") return;
  errorHandler = (evt: ErrorEvent) => reportFromErrorEvent(evt);
  rejectionHandler = (evt: PromiseRejectionEvent) =>
    reportFromRejectionEvent(evt);
  window.addEventListener("error", errorHandler);
  window.addEventListener("unhandledrejection", rejectionHandler);
  installed = true;
}

/**
 * Test-only: remove listeners. Production code should never need to
 * uninstall — the reporter lives for the page lifetime.
 */
export function uninstallErrorReporterForTests(): void {
  if (!installed || typeof window === "undefined") return;
  if (errorHandler) window.removeEventListener("error", errorHandler);
  if (rejectionHandler)
    window.removeEventListener("unhandledrejection", rejectionHandler);
  errorHandler = null;
  rejectionHandler = null;
  installed = false;
}

/** Test-only: reset module state between tests. */
export function resetErrorReporterForTests(): void {
  uninstallErrorReporterForTests();
  lastApiRequestId = null;
  inFlight = false;
}

/** Direct entry point used by ErrorBoundary. */
export function report(error: unknown, componentStack?: string | null): void {
  const message = extractMessage(error);
  const stack = combineStack(extractStack(error), componentStack);
  send({ message, stack });
}

/**
 * Translate a synthetic ErrorEvent into a sanitised report. Exported for
 * the unit tests; production code routes through the installed handler.
 */
export function reportFromErrorEvent(evt: ErrorEvent): void {
  const message = evt.message || extractMessage(evt.error) || "unknown error";
  const stack = extractStack(evt.error);
  send({ message, stack });
}

/** Translate a PromiseRejectionEvent. Exported for unit tests. */
export function reportFromRejectionEvent(evt: PromiseRejectionEvent): void {
  const reason = evt.reason;
  const message =
    "Unhandled promise rejection: " + (extractMessage(reason) || String(reason));
  const stack = extractStack(reason);
  send({ message, stack });
}

interface ReportInput {
  message: string;
  stack: string | null;
}

function send(input: ReportInput): void {
  // Re-entrancy guard. If a previous send is in flight (e.g. a long-lived
  // fetch), we drop new reports rather than queue — this is a best-effort
  // sink, not a reliable transport. Critically, when the in-flight POST
  // fails it does NOT trigger another POST.
  if (inFlight) return;
  inFlight = true;

  const payload: {
    message: string;
    stack?: string;
    requestId?: string;
    url: string;
    userAgent: string;
  } = {
    message: input.message,
    url: typeof window !== "undefined" ? window.location.href : "",
    userAgent:
      typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
  };
  if (input.stack) payload.stack = input.stack;
  if (lastApiRequestId) payload.requestId = lastApiRequestId;

  // Fire-and-forget. The .catch swallows ALL failures so we can't end up
  // looping on our own POST throwing.
  try {
    void fetch(resolveUrl("/api/errors"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    })
      .catch(() => {
        // intentional silent drop — see module docstring
      })
      .finally(() => {
        inFlight = false;
      });
  } catch {
    // Synchronous failure (extremely rare); release the latch.
    inFlight = false;
  }
}

function extractMessage(err: unknown): string {
  if (err == null) return "";
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    const maybe = err as { message?: unknown };
    if (typeof maybe.message === "string") return maybe.message;
  }
  return "";
}

function extractStack(err: unknown): string | null {
  if (err == null) return null;
  if (err instanceof Error && err.stack) return err.stack;
  if (typeof err === "object") {
    const maybe = err as { stack?: unknown };
    if (typeof maybe.stack === "string") return maybe.stack;
  }
  return null;
}

function combineStack(
  errStack: string | null,
  componentStack?: string | null,
): string | null {
  if (errStack && componentStack) {
    return `${errStack}\n--- componentStack ---\n${componentStack}`;
  }
  return errStack ?? componentStack ?? null;
}
